
<template>
  <div class="payment-stats-container">
    <div class="stats-header">
      <div class="header-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      </div>
      <div class="header-content">
        <h1>Fizetési Statisztikák</h1>
        <p>Fizetések összesítése csoportok, tanárok és egyéb szempontok szerint</p>
      </div>
      <button @click="downloadXLSX" class="download-btn">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7,10 12,15 17,10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        XLSX Letöltés
      </button>
    </div>

      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="summary-card overall">
          <div class="card-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div class="card-content">
            <h3>Összesített</h3>
            <div class="stats-grid">
              <div class="stat-item">
                <span class="label">Összes bevétel</span>
                <span class="value">{{ formatPrice(overallStats.combinedRevenue) }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Fizetett összeg</span>
                <span class="value">{{ formatPrice(overallStats.combinedPaid) }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Hátralék</span>
                <span class="value">{{ formatPrice(overallStats.totalDue) }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Kiadások</span>
                <span class="value">{{ formatPrice(overallStats.combinedExpenses) }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Nettó nyereség</span>
                <span class="value">{{ formatPrice(overallStats.netProfit) }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Aktív táncosok</span>
                <span class="value">{{ overallStats.activeChildren }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Breakdown: billing system vs receipts -->
        <div class="summary-card breakdown">
          <div class="card-content">
            <h3>Rendszer szerinti bontás</h3>
            <div class="breakdown-grid">
              <div class="breakdown-col">
                <h4>Bérletes befizetések</h4>
                <div class="stat-item">
                  <span class="label">Bevétel</span>
                  <span class="value">{{ formatPrice(overallStats.totalRevenue) }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Fizetett</span>
                  <span class="value">{{ formatPrice(overallStats.totalPaid) }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Kiadások</span>
                  <span class="value">{{ formatPrice(overallStats.totalExpenses) }}</span>
                </div>
              </div>
              <div class="breakdown-col">
                <h4>Számlázás (táncosok)</h4>
                <div class="stat-item">
                  <span class="label">Számlázott összeg</span>
                  <span class="value">{{ formatPrice(overallStats.billingRevenue) }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Egyenleg feltöltések</span>
                  <span class="value">{{ formatPrice(overallStats.walletTopups) }}</span>
                </div>
              </div>
              <div class="breakdown-col">
                <h4>Számlák (bevásárlás)</h4>
                <div class="stat-item">
                  <span class="label">Számlák összesen</span>
                  <span class="value">{{ formatPrice(overallStats.receiptsTotal) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    <!-- Filters and Search -->
    <div class="filters-section">

      <!-- Date Filters -->
      <div class="date-filters">
        <div class="date-filter-group">
          <label>Időszak szűrő:</label>
          <select v-model="dateFilter" @change="onDateFilterChange" class="filter-select">
            <option value="all">Összes időszak</option>
            <option value="week">Ez a hét</option>
            <option value="month">Ez a hónap</option>
            <option value="year">Ez az év</option>
            <option value="custom">Egyedi időszak</option>
          </select>
        </div>

        <div v-if="dateFilter === 'custom'" class="custom-date-inputs">
          <div class="date-input-group">
            <label>Kezdő dátum:</label>
            <input 
              type="date" 
              v-model="customStartDate" 
              @change="loadStats"
              class="filter-select"
            />
          </div>
          <div class="date-input-group">
            <label>Vég dátum:</label>
            <input 
              type="date" 
              v-model="customEndDate" 
              @change="loadStats"
              class="filter-select"
            />
          </div>
        </div>

        <div v-if="dateFilter === 'month'" class="month-year-selector">
          <div class="date-input-group">
            <label>Hónap:</label>
            <select v-model="selectedMonth" @change="loadStats" class="filter-select">
              <option value="1">Január</option>
              <option value="2">Február</option>
              <option value="3">Március</option>
              <option value="4">Április</option>
              <option value="5">Május</option>
              <option value="6">Június</option>
              <option value="7">Július</option>
              <option value="8">Augusztus</option>
              <option value="9">Szeptember</option>
              <option value="10">Október</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>
          <div class="date-input-group">
            <label>Év:</label>
            <select v-model="selectedYear" @change="loadStats" class="filter-select">
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Tabs -->
    <div class="stats-tabs">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        @click="activeTab = tab.id"
        :class="['tab-button', { active: activeTab === tab.id }]"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path :d="tab.icon"/>
        </svg>
        {{ tab.name }}
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">

      <!-- Group Statistics -->
      <div v-if="activeTab === 'groups'" class="stats-section">
        <div class="section-header">
          <h2>Csoport Statisztikák</h2>
        </div>
        
        <!-- Groups Search -->
        <div class="tab-search">
          <div class="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              class="modern-input"
              placeholder="Keresés csoport neve alapján..."
              v-model="groupsSearchQuery"
              @input="filterGroups"
            />
          </div>
        </div>
        
        <div class="groups-grid">
           <div v-for="group in filteredGroupStats" :key="group.id" class="group-card">
             <div class="group-header">
               <h3>{{ group.name }}</h3>
               <span class="group-count">{{ group.childrenCount }} táncos</span>
             </div>
             <div class="group-stats">
               <div class="group-stat">
                 <span class="label">Összes bevétel</span>
                 <span class="value">{{ formatPrice(group.totalRevenue) }}</span>
               </div>
               <div class="group-stat">
                 <span class="label">Fizetett</span>
                 <span class="value">{{ formatPrice(group.totalPaid) }}</span>
               </div>
               <div class="group-stat">
                 <span class="label">Feltöltött</span>
                 <span class="value">{{ formatPrice(group.totalWalletTopups) }}</span>
               </div>
               <div class="group-stat">
                 <span class="label">Hátralék</span>
                 <span class="value">{{ formatPrice(group.totalDue) }}</span>
               </div>
             </div>
           </div>
        </div>
      </div>

      <!-- Teacher Statistics -->
      <div v-if="activeTab === 'teachers'" class="stats-section">
        <div class="section-header">
          <h2>Tanár Statisztikák</h2>
        </div>

        <!-- Teacher Summary -->
        <div class="teacher-summary">
          <div class="summary-card">
            <div class="card-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div class="card-content">
              <h3>Tanárok Összesített</h3>
              <div class="stats-grid">
                <div class="stat-item">
                  <span class="label">Tanárok száma</span>
                  <span class="value">{{ teacherStats.length }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Összes bevétel</span>
                  <span class="value">{{ formatPrice(teacherStats.reduce((sum, t) => sum + t.totalRevenue, 0)) }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Tanár kifizetések</span>
                  <span class="value">{{ formatPrice(teacherStats.reduce((sum, t) => sum + t.totalPaid, 0)) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Teachers Search -->
        <div class="tab-search">
          <div class="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              class="modern-input"
              placeholder="Keresés tanár neve alapján..."
              v-model="teachersSearchQuery"
              @input="filterTeachers"
            />
          </div>
        </div>
        
         <div class="teachers-grid">
           <div v-for="teacher in filteredTeacherStats" :key="teacher.id" class="teacher-card">
             <div class="teacher-header">
               <h3>{{ teacher.name }}</h3>
               <span class="teacher-groups">{{ teacher.groupsCount }} csoport</span>
             </div>
             <div class="teacher-stats">
               <div class="teacher-stat">
                 <span class="label">Összes bevétel</span>
                 <span class="value">{{ formatPrice(teacher.totalRevenue) }}</span>
               </div>
               <div class="teacher-stat">
                 <span class="label">Számlázás (táncosok)</span>
                 <span class="value">{{ formatPrice(teacher.totalBilling) }}</span>
               </div>
               <div class="teacher-stat">
                 <span class="label">Feltöltött</span>
                 <span class="value">{{ formatPrice(teacher.totalWalletTopups) }}</span>
               </div>
               <div class="teacher-stat">
                 <span class="label">Fizetett</span>
                 <span class="value">{{ formatPrice(teacher.totalPaid) }}</span>
               </div>
               <div class="teacher-stat">
                 <span class="label">Hátralék</span>
                 <span class="value">{{ formatPrice(teacher.totalDue) }}</span>
               </div>
             </div>
           </div>
         </div>
      </div>

      <!-- Children List -->
      <div v-if="activeTab === 'children'" class="stats-section">
        <div class="section-header">
          <h2>Táncosok Fizetési Adatai</h2>
          <div class="children-count">
            {{ filteredChildren.length }} táncos
          </div>
        </div>
        
        <!-- Children Search -->
        <div class="tab-search">
          <div class="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              class="modern-input"
              placeholder="Keresés táncos neve vagy szülő email címe alapján..."
              v-model="childrenSearchQuery"
              @input="filterChildren"
            />
          </div>
          <select v-model="selectedStatus" @change="loadStats" class="filter-select">
            <option value="">Összes státusz</option>
            <option value="completed">Teljesítve</option>
            <option value="partial">Részleges</option>
            <option value="pending">Függőben</option>
          </select>
        </div>

        <!-- Group / Teacher summary (statistics only) -->
        <div class="children-summary">
          <div class="summary-block">
            <h4>Csoportonkénti összesítés</h4>
            <div class="summary-chips">
              <div v-for="g in childGroupSummary" :key="'g'+g.key" class="summary-chip">
                <span class="chip-label">{{ g.label }}</span>
                <span class="chip-paid">{{ formatPrice(g.paid) }}</span>
                <span class="chip-billing">{{ formatPrice(g.billing) }}</span>
              </div>
              <div v-if="childGroupSummary.length === 0" class="no-summary">Nincs adat</div>
            </div>
          </div>
          <div class="summary-block">
            <h4>Tanáronkénti összesítés</h4>
            <div class="summary-chips">
              <div v-for="t in childTeacherSummary" :key="'t'+t.key" class="summary-chip">
                <span class="chip-label">{{ t.label }}</span>
                <span class="chip-paid">{{ formatPrice(t.paid) }}</span>
                <span class="chip-billing">{{ formatPrice(t.billing) }}</span>
              </div>
              <div v-if="childTeacherSummary.length === 0" class="no-summary">Nincs adat</div>
            </div>
          </div>
        </div>

        <div class="children-table">
            <div class="table-header">
              <div class="col-expand"></div>
              <div class="col-name">Táncos neve</div>
              <div class="col-group">Csoport</div>
              <div class="col-teacher">Tanár</div>
              <div class="col-paid">Fizetett</div>
              <div class="col-billing">Számlázás</div>
              <div class="col-due">Hátralék</div>
              <div class="col-status">Státusz</div>
              <div class="col-actions">Műveletek</div>
            </div>
           <div class="table-body">
             <template v-for="child in filteredChildren" :key="child.id">
               <div class="table-row">
                 <div class="col-expand">
                   <button
                     @click="toggleChildExpansion(child.id)"
                     class="btn-expand"
                     :class="{ expanded: expandedChildren.has(child.id) }"
                     v-if="child.payments && child.payments.length > 0"
                   >
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                       <path d="M9 18l6-6-6-6"/>
                     </svg>
                   </button>
                 </div>
                 <div class="col-name">
                   <div class="child-info">
                     <div class="child-name">{{ child.name }}</div>
                     <div class="child-details">{{ child.parentEmail }}</div>
                   </div>
                 </div>
                 <div class="col-group">
                   <span class="group-badge">{{ child.groupName || 'Nincs csoport' }}</span>
                 </div>
                 <div class="col-teacher">
                   <span class="teacher-name">{{ child.teacherName || 'Nincs hozzárendelve' }}</span>
                 </div>
                  <div class="col-paid">
                    <span class="amount paid">{{ formatPrice(child.totalPaid) }}</span>
                  </div>
                   <div class="col-billing">
                     <span class="amount billing" :title="'Számla: ' + formatPrice(child.totalBilling) + ' / Feltöltés: ' + formatPrice(child.walletTopups)">
                       {{ formatPrice(child.totalBilling || 0) }}
                     </span>
                   </div>
                  <div class="col-due">
                    <span class="amount due">{{ formatPrice(child.totalDue) }}</span>
                  </div>
                 <div class="col-status">
                   <span :class="['status-badge', child.status]">{{ getStatusText(child.status) }}</span>
                 </div>
                 <div class="col-actions">
                   <button @click="viewChildPayments(child.id)" class="btn-action btn-details">
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                       <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                       <circle cx="12" cy="12" r="3"/>
                     </svg>
                     Részletek
                   </button>
                 </div>
               </div>
               <!-- Expanded payment details -->
               <div v-if="expandedChildren.has(child.id)" class="payment-details-row">
                 <div class="payment-details-content">
                   <h4>Fizetési előzmények</h4>
                   <div class="payments-list">
                     <div v-for="payment in child.payments" :key="payment.id" class="payment-item">
                       <div class="payment-info">
                          <div class="payment-type">{{ payment.ticket_name || payment.payment_method || 'Fizetés' }}</div>
                         <div class="payment-date">{{ formatDate(payment.payment_date) }}</div>
                         <div class="payment-method">{{ getPaymentMethodText(payment.payment_method) }}</div>
                       </div>
                       <div class="payment-amounts">
                         <div class="payment-paid">Fizetett: {{ formatPrice(payment.amount_paid) }}</div>
                         <div class="payment-due" v-if="payment.amount_due > 0">Hátralék: {{ formatPrice(payment.amount_due) }}</div>
                       </div>
                       <div class="payment-notes" v-if="payment.notes">
                         <small>{{ payment.notes }}</small>
                       </div>
                     </div>
                     <div v-if="!child.payments || child.payments.length === 0" class="no-payments">
                       Nincs fizetési előzmény
                     </div>
                   </div>
                 </div>
               </div>
             </template>
           </div>
         </div>
         </div>
       <!-- Expenses Section -->
       <div v-if="activeTab === 'expenses'" class="stats-section">
         <div class="section-header">
           <h2>Kiadások Statisztikái</h2>
        
         </div>

         <!-- Expense Statistics Cards -->
         <div class="expense-stats-cards">
           <div class="stats-card">
             <div class="card-icon">
               <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                 <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
               </svg>
             </div>
             <div class="card-content">
               <h3>Összes kiadás</h3>
               <div class="stat-value">{{ formatPrice(expenseStats.totalExpenses) }}</div>
             </div>
           </div>

           <div class="stats-card">
             <div class="card-icon">
               <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                 <path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
               </svg>
             </div>
             <div class="card-content">
               <h3>Kiadások száma</h3>
               <div class="stat-value">{{ expenseStats.expenseCount }}</div>
             </div>
           </div>

           <div class="stats-card">
             <div class="card-icon">
               <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                 <path d="M3 3v18h18"/>
                 <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
               </svg>
             </div>
             <div class="card-content">
               <h3>Kategóriák</h3>
               <div class="stat-value">{{ Object.keys(expenseStats.byCategory).length }}</div>
             </div>
           </div>
         </div>

         <!-- Expenses by Category Chart -->
         <div class="chart-section">
           <h3>Kiadások kategóriák szerint</h3>
           <div class="category-chart">
             <div v-for="(amount, category) in expenseStats.byCategory" :key="category" class="chart-bar">
               <div class="bar-label">{{ category }}</div>
               <div class="bar-container">
                 <div class="bar-fill" :style="{ width: getBarWidth(amount) }"></div>
                 <div class="bar-value">{{ formatPrice(amount) }}</div>
               </div>
             </div>
           </div>
         </div>

         <!-- Monthly Trend Chart -->
         <div class="chart-section">
           <h3>Havi trend (utolsó 12 hónap)</h3>
           <div class="monthly-chart">
             <div v-for="month in expenseStats.monthlyTrend" :key="month.month" class="chart-bar">
               <div class="bar-label">{{ formatMonthLabel(month.month) }}</div>
               <div class="bar-container">
                 <div class="bar-fill" :style="{ width: getMonthlyBarWidth(month.total) }"></div>
                 <div class="bar-value">{{ formatPrice(month.total) }}</div>
               </div>
             </div>
           </div>
         </div>

         <!-- Expenses Search and List -->
         <div class="tab-search">
           <div class="search-input-wrapper">
             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
               <circle cx="11" cy="11" r="8"/>
               <path d="M21 21l-4.35-4.35"/>
             </svg>
             <input
               type="text"
               class="modern-input"
               placeholder="Keresés leírás vagy kategória alapján..."
               v-model="expensesSearchQuery"
               @input="filterExpenses"
             />
           </div>
         </div>

         <div class="expenses-table">
           <div class="table-header">
             <div class="col-description">Leírás</div>
             <div class="col-category">Kategória</div>
             <div class="col-amount">Összeg</div>
             <div class="col-adjusted">Korrigált összeg</div>
             <div class="col-date">Dátum</div>
             <div class="col-actions">Műveletek</div>
           </div>
           <div class="table-body">
             <div v-if="filteredExpenses.length === 0" class="no-data">
               <div class="no-data-content">
                 <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" opacity="0.5">
                   <path d="M12 5v14M5 12h14"/>
                 </svg>
                 <p>Nincsenek kiadások</p>
               </div>
             </div>
             <div v-for="expense in filteredExpenses" :key="expense.id" class="table-row">
               <div class="col-description">{{ expense.description }}</div>
               <div class="col-category">
                 <span class="category-badge">{{ expense.category || 'Egyéb' }}</span>
               </div>
               <div class="col-amount">
                 <span class="amount expense">{{ formatPrice(expense.amount) }}</span>
               </div>
               <div class="col-date">{{ formatDate(expense.date) }}</div>
               
             </div>
           </div>
         </div>
       </div>

        <!-- Expense Modal -->
        <div v-if="showExpenseModal" class="mdl-overlay" @click="closeExpenseModal">
          <div class="mdl-content" @click.stop>
            <div class="mdl-header">
              <h3>{{ editingExpense ? 'Kiadás szerkesztése' : 'Új kiadás hozzáadása' }}</h3>
              <button @click="closeExpenseModal" class="close-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="mdl-body">
              <form @submit.prevent="saveExpense">
                <div class="form-group">
                  <label for="expenseDescription">Leírás:</label>
                  <input
                    id="expenseDescription"
                    v-model="expenseForm.description"
                    type="text"
                    required
                    placeholder="Kiadás leírása..."
                  />
                </div>

                <div class="form-group">
                  <label for="expenseAmount">Összeg (Ft):</label>
                  <input
                    id="expenseAmount"
                    v-model.number="expenseForm.amount"
                    type="number"
                    step="0.01"
                    required
                    placeholder="0.00"
                  />
                </div>

                <div class="form-group">
                  <label for="expenseMultiplier">Szorzó:</label>
                  <input
                    id="expenseMultiplier"
                    v-model.number="expenseForm.multiplier"
                    type="number"
                    step="0.01"
                    min="0"
                    max="2"
                    placeholder="1.00"
                  />
                </div>

                <div class="form-group">
                  <label for="expenseDate">Dátum:</label>
                  <input
                    id="expenseDate"
                    v-model="expenseForm.date"
                    type="date"
                    required
                  />
                </div>

                <div class="form-group">
                  <label for="expenseCategory">Kategória:</label>
                  <select id="expenseCategory" v-model="expenseForm.category">
                    <option value="">Válassz kategóriát...</option>
                    <option value="Benzin">Benzin</option>
                    <option value="Bérleti díj">Bérleti díj</option>
                    <option value="Karbantartás">Karbantartás</option>
                    <option value="Alkalmazottak">Alkalmazottak</option>
                    <option value="Egyéb">Egyéb</option>
                  </select>
                </div>

                <div class="form-actions">
                  <button type="button" @click="closeExpenseModal" class="btn-secondary">Mégse</button>
                  <button type="submit" class="btn-primary" :disabled="savingExpense">
                    {{ savingExpense ? 'Mentés...' : (editingExpense ? 'Frissítés' : 'Hozzáadás') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import axios from 'axios';
import toastMixin from '../mixins/toast.js';

export default {
  mixins: [toastMixin],
  data() {
    return {
      // Statistics data
      overallStats: {
        totalRevenue: 0,
        totalPaid: 0,
        totalDue: 0,
        activeChildren: 0,
        billingRevenue: 0,
        walletTopups: 0,
        receiptsTotal: 0,
        combinedRevenue: 0,
        combinedPaid: 0,
        combinedExpenses: 0
      },
      groupStats: [],
      teacherStats: [],
      children: [],
      filteredChildren: [],
      
      // Filters
      searchQuery: '',
      selectedGroup: '',
      selectedTeacher: '',
      selectedStatus: '',
      
      // Tab-specific search queries
      childrenSearchQuery: '',
      groupsSearchQuery: '',
      teachersSearchQuery: '',
      
      // Date filters
      dateFilter: 'all', // 'all', 'week', 'month', 'year', 'custom'
      customStartDate: '',
      customEndDate: '',
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      
      // UI state
      activeTab: 'groups',
      loading: false,
      expandedChildren: new Set(), // Track which children have expanded payment details
      
      // Data for dropdowns
      groups: [],
      teachers: [],
      
      // Tabs configuration
      tabs: [
        { id: 'groups', name: 'Csoportok', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' },
        { id: 'teachers', name: 'Tanárok', icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0' },
        { id: 'children', name: 'Táncosok', icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0' },
        { id: 'expenses', name: 'Kiadások', icon: 'M12 5v14M5 12h14' }
      ],
      
      // Chart data
      revenueBreakdown: [],
      paymentStatus: {
        completed: 0,
        partial: 0,
        pending: 0
      },

      saving: false,

      // Expense modal state
      showExpenseModal: false,
      editingExpense: null,
      expenses: [],
      filteredExpenses: [],
      expensesSearchQuery: '',
      expenseForm: {
        description: '',
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        category: '',
        multiplier: 1
      },
      savingExpense: false,

      // Expense statistics
      expenseStats: {
        totalExpenses: 0,
        expenseCount: 0,
        byCategory: {},
        monthlyTrend: []
      }
    };
  },
  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear - 5; i <= currentYear + 1; i++) {
        years.push(i);
      }
      return years;
    },
    
    filteredGroupStats() {
      if (!this.groupsSearchQuery || !this.groupsSearchQuery.trim()) {
        return this.groupStats;
      }
      const query = this.groupsSearchQuery.toLowerCase().trim();
      return this.groupStats.filter(group =>
        group.name && group.name.toLowerCase().includes(query)
      );
    },
    
    filteredTeacherStats() {
      if (!this.teachersSearchQuery || !this.teachersSearchQuery.trim()) {
        return this.teacherStats;
      }
      const query = this.teachersSearchQuery.toLowerCase().trim();
      return this.teacherStats.filter(teacher =>
        teacher.name && teacher.name.toLowerCase().includes(query)
      );
    },

    // Group summary derived from the children list (statistics only).
    childGroupSummary() {
      const map = {};
      for (const c of this.filteredChildren) {
        const key = c.groupId || 0;
        const label = c.groupName || 'Nincs csoport';
        if (!map[key]) map[key] = { key, label, paid: 0, billing: 0 };
        map[key].paid += c.totalPaid || 0;
        map[key].billing += c.totalBilling || 0;
      }
      return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
    },

    // Teacher summary derived from the children list (statistics only).
    childTeacherSummary() {
      const map = {};
      for (const c of this.filteredChildren) {
        const key = c.teacherId || 0;
        const label = c.teacherName || 'Nincs hozzárendelve';
        if (!map[key]) map[key] = { key, label, paid: 0, billing: 0 };
        map[key].paid += c.totalPaid || 0;
        map[key].billing += c.totalBilling || 0;
      }
      return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
    }
  },
  created() {
    this.loadStats();
    this.loadGroups();
    this.loadTeachers();
    this.loadExpenses();
    this.loadExpenseStats();
  },
  methods: {
    async loadStats() {
      this.loading = true;
      try {
        // Get date parameters
        const dateParams = this.getDateParameters();
        
        // Load overall statistics
        const overallResponse = await axios.get(this.$BACKEND_HOST + '/api/pricing/statistics/overall', {
          params: dateParams
        });
        console.log('Overall stats:', overallResponse.data);
        this.overallStats = overallResponse.data.stats;
        
        // Ensure we have valid data
        if (!this.overallStats) {
          this.overallStats = {
            totalRevenue: 0,
            totalPaid: 0,
            totalDue: 0,
            activeChildren: 0
          };
        }
        
        // Load group statistics
        const groupsResponse = await axios.get(this.$BACKEND_HOST + '/api/pricing/statistics/groups', {
          params: dateParams
        });
        console.log('Group stats:', groupsResponse.data);
        this.groupStats = groupsResponse.data.stats || [];
        
        // Load teacher statistics
        const teachersResponse = await axios.get(this.$BACKEND_HOST + '/api/pricing/statistics/teachers', {
          params: dateParams
        });
        console.log('Teacher stats:', teachersResponse.data);
        this.teacherStats = teachersResponse.data.stats || [];
        
        // Load children data
        const childrenResponse = await axios.get(this.$BACKEND_HOST + '/api/pricing/statistics/children', {
          params: dateParams
        });
        console.log('Children stats:', childrenResponse.data);
        this.children = childrenResponse.data.children || [];
        this.filteredChildren = [...this.children];
        
        // Apply current filters to the new data
        this.filterChildren();
        
        // Calculate breakdown data
        this.calculateRevenueBreakdown();
        this.calculatePaymentStatus();
        
      } catch (error) {
        console.error('Error loading statistics:', error);
        this.showToast('Hiba a statisztikák betöltése során', 'error');
        
        // Set default values on error
        this.overallStats = {
          totalRevenue: 0,
          totalPaid: 0,
          totalDue: 0,
          totalExpenses: 0,
          netProfit: 0,
          activeChildren: 0,
          billingRevenue: 0,
          walletTopups: 0,
          receiptsTotal: 0,
          combinedRevenue: 0,
          combinedPaid: 0,
          combinedExpenses: 0
        };
        this.groupStats = [];
        this.teacherStats = [];
        this.children = [];
        this.filteredChildren = [];
      }
      this.loading = false;
    },
    
    async loadGroups() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/settings/groups/info');
        this.groups = response.data.groups;
      } catch (error) {
        console.error('Error loading groups:', error);
      }
    },
    
    async loadTeachers() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/settings/teachers/info');
        this.teachers = response.data.teachers.map(teacher => ({
          id: teacher.id,
          name: `${teacher.family_name} ${teacher.sure_name}`
        }));
      } catch (error) {
        console.error('Error loading teachers:', error);
      }
    },
    
    getDateParameters() {
      const params = {};
      
      if (this.dateFilter === 'week') {
        const now = new Date();
        const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
        const endOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 6));
        params.start_date = startOfWeek.toISOString().split('T')[0];
        params.end_date = endOfWeek.toISOString().split('T')[0];
      } else if (this.dateFilter === 'month') {
        const startOfMonth = new Date(this.selectedYear, this.selectedMonth - 1, 1);
        const endOfMonth = new Date(this.selectedYear, this.selectedMonth, 0);
        params.start_date = startOfMonth.toISOString().split('T')[0];
        params.end_date = endOfMonth.toISOString().split('T')[0];
      } else if (this.dateFilter === 'year') {
        const startOfYear = new Date(this.selectedYear, 0, 1);
        const endOfYear = new Date(this.selectedYear, 11, 31);
        params.start_date = startOfYear.toISOString().split('T')[0];
        params.end_date = endOfYear.toISOString().split('T')[0];
      } else if (this.dateFilter === 'custom') {
        if (this.customStartDate) {
          params.start_date = this.customStartDate;
        }
        if (this.customEndDate) {
          params.end_date = this.customEndDate;
        }
      }
      
      return params;
    },
    
    onDateFilterChange() {
      // Reset custom dates when changing filter type
      if (this.dateFilter !== 'custom') {
        this.customStartDate = '';
        this.customEndDate = '';
      }
      this.loadStats();
      this.loadExpenseStats();
    },
    
    filterChildren() {
      let filtered = [...this.children];
      
      // Search filter (children-specific)
      if (this.childrenSearchQuery && this.childrenSearchQuery.trim()) {
        const query = this.childrenSearchQuery.toLowerCase().trim();
        filtered = filtered.filter(child =>
          (child.name && child.name.toLowerCase().includes(query)) ||
          (child.parentEmail && child.parentEmail.toLowerCase().includes(query))
        );
      }
      
      // Group filter
      if (this.selectedGroup) {
        const groupId = parseInt(this.selectedGroup);
        filtered = filtered.filter(child => child.groupId && child.groupId === groupId);
      }
      
      // Teacher filter
      if (this.selectedTeacher) {
        filtered = filtered.filter(child => child.teacherId == this.selectedTeacher);
      }
      
      // Status filter
      if (this.selectedStatus) {
        filtered = filtered.filter(child => child.status === this.selectedStatus);
      }
      
      this.filteredChildren = filtered;
    },
    
    filterGroups() {
      // This is handled by computed property
    },
    
    filterTeachers() {
      // This is handled by computed property
    },
    
    calculateRevenueBreakdown() {
      const breakdown = {};
      let totalAmount = 0;
      
      this.children.forEach(child => {
        if (child.payments && Array.isArray(child.payments)) {
          child.payments.forEach(payment => {
            const label = payment.ticket_name || payment.payment_method || 'Fizetés';
            const amount = parseFloat(payment.amountPaid) || 0;
            
            if (!breakdown[label]) {
              breakdown[label] = 0;
            }
            breakdown[label] += amount;
            totalAmount += amount;
          });
        }
      });
      
      // Convert to array and calculate percentages
      this.revenueBreakdown = Object.entries(breakdown).map(([type, value]) => ({
        label: type,
        value: value,
        percentage: totalAmount > 0 ? (value / totalAmount) * 100 : 0
      }));
      
      // Sort by value descending
      this.revenueBreakdown.sort((a, b) => b.value - a.value);
    },
    
    calculatePaymentStatus() {
      this.paymentStatus = {
        completed: this.children.filter(child => child.status === 'completed').length,
        partial: this.children.filter(child => child.status === 'partial').length,
        pending: this.children.filter(child => child.status === 'pending').length
      };
    },
    
    viewChildPayments(childId) {
      // Navigate to child details page
      this.$router.push(`/children/${childId}`);
    },
    
    formatPrice(price) {
      // Handle undefined, null, or NaN values
      if (price === undefined || price === null || isNaN(price)) {
        return '0 Ft';
      }
      
      // Convert to number if it's a string
      const numPrice = typeof price === 'string' ? parseFloat(price) : price;
      
      // Check if it's a valid number
      if (isNaN(numPrice)) {
        return '0 Ft';
      }
      
      return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF'
      }).format(numPrice);
    },
    
    getStatusText(status) {
      const statusMap = {
        'completed': 'Teljesítve',
        'partial': 'Részleges',
        'pending': 'Függőben'
      };
      return statusMap[status] || status;
    },

    // Expense methods
    async loadExpenses() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/pricing/expenses');
        this.expenses = response.data.expenses || [];
        this.filteredExpenses = [...this.expenses];
        this.filterExpenses();
      } catch (error) {
        console.error('Error loading expenses:', error);
        this.showToast('Hiba a kiadások betöltése során', 'error');
      }
    },

    filterExpenses() {
      if (!this.expensesSearchQuery || !this.expensesSearchQuery.trim()) {
        this.filteredExpenses = [...this.expenses];
        return;
      }
      const query = this.expensesSearchQuery.toLowerCase().trim();
      this.filteredExpenses = this.expenses.filter(expense =>
        (expense.description && expense.description.toLowerCase().includes(query)) ||
        (expense.category && expense.category.toLowerCase().includes(query))
      );
    },

    openExpenseModal(expense = null) {
      this.editingExpense = expense;
      if (expense) {
        this.expenseForm = {
          description: expense.description,
          amount: expense.amount,
          date: expense.date,
          category: expense.category
        };
      } else {
        this.expenseForm = {
          description: '',
          amount: 0,
          date: new Date().toISOString().split('T')[0],
          category: ''
        };
      }
      this.showExpenseModal = true;
    },

    closeExpenseModal() {
      this.showExpenseModal = false;
      this.editingExpense = null;
      this.expenseForm = {
        description: '',
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        category: '',
        multiplier: 1
      };
    },

    async saveExpense() {
      this.savingExpense = true;
      try {
        if (this.editingExpense) {
          await axios.put(this.$BACKEND_HOST + `/api/pricing/expenses/${this.editingExpense.id}`, this.expenseForm);
          this.showToast('Kiadás sikeresen frissítve!', 'success');
        } else {
          await axios.post(this.$BACKEND_HOST + '/api/pricing/expenses', this.expenseForm);
          this.showToast('Kiadás sikeresen hozzáadva!', 'success');
        }
        this.closeExpenseModal();
        await this.loadExpenses();
        await this.loadExpenseStats();
        await this.loadStats(); // Refresh overall stats
      } catch (error) {
        console.error('Error saving expense:', error);
        this.showToast('Hiba történt a kiadás mentése során', 'error');
      }
      this.savingExpense = false;
    },

    async deleteExpense(expenseId) {
      if (!confirm('Biztosan törölni szeretné ezt a kiadást?')) {
        return;
      }

      try {
        await axios.delete(this.$BACKEND_HOST + `/api/pricing/expenses/${expenseId}`);
        this.showToast('Kiadás sikeresen törölve!', 'success');
        await this.loadExpenses();
        await this.loadExpenseStats();
        await this.loadStats(); // Refresh overall stats
      } catch (error) {
        console.error('Error deleting expense:', error);
        this.showToast('Hiba történt a kiadás törlése során', 'error');
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('hu-HU');
    },

    toggleChildExpansion(childId) {
      if (this.expandedChildren.has(childId)) {
        this.expandedChildren.delete(childId);
      } else {
        this.expandedChildren.add(childId);
      }
      // Force reactivity update
      this.expandedChildren = new Set(this.expandedChildren);
    },

    getPaymentMethodText(method) {
      const methodMap = {
        'cash': 'Készpénz',
        'card': 'Bankkártya',
        'transfer': 'Átutalás',
        'other': 'Egyéb'
      };
      return methodMap[method] || method;
    },

    // Expense statistics methods
    async loadExpenseStats() {
      try {
        const dateParams = this.getDateParameters();
        const response = await axios.get(this.$BACKEND_HOST + '/api/pricing/expenses/stats', {
          params: dateParams
        });
        this.expenseStats = response.data;
      } catch (error) {
        console.error('Error loading expense stats:', error);
        this.expenseStats = {
          totalExpenses: 0,
          expenseCount: 0,
          byCategory: {},
          monthlyTrend: []
        };
      }
    },

    getBarWidth(amount) {
      const maxAmount = Math.max(...Object.values(this.expenseStats.byCategory));
      return maxAmount > 0 ? `${(amount / maxAmount) * 100}%` : '0%';
    },

    getMonthlyBarWidth(amount) {
      const maxAmount = Math.max(...this.expenseStats.monthlyTrend.map(m => m.total));
      return maxAmount > 0 ? `${(amount / maxAmount) * 100}%` : '0%';
    },

    formatMonthLabel(monthYear) {
      const [year, month] = monthYear.split('-');
      const monthNames = [
        'Jan', 'Feb', 'Már', 'Ápr', 'Máj', 'Jún',
        'Júl', 'Aug', 'Szept', 'Okt', 'Nov', 'Dec'
      ];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    },

    // Method to download data as XLSX
    downloadXLSX() {
      // Get current date parameters
      const dateParams = this.getDateParameters();
      const params = new URLSearchParams();

      // Add date parameters to query string
      if (dateParams.start_date) {
        params.append('start_date', dateParams.start_date);
      }
      if (dateParams.end_date) {
        params.append('end_date', dateParams.end_date);
      }

      // Add search and filter parameters to ensure download reflects current filters
      if (this.childrenSearchQuery && this.childrenSearchQuery.trim()) {
        params.append('children_search', this.childrenSearchQuery.trim());
      }
      if (this.groupsSearchQuery && this.groupsSearchQuery.trim()) {
        params.append('groups_search', this.groupsSearchQuery.trim());
      }
      if (this.teachersSearchQuery && this.teachersSearchQuery.trim()) {
        params.append('teachers_search', this.teachersSearchQuery.trim());
      }
      if (this.expensesSearchQuery && this.expensesSearchQuery.trim()) {
        params.append('expenses_search', this.expensesSearchQuery.trim());
      }
      if (this.selectedGroup) {
        params.append('group_id', this.selectedGroup);
      }
      if (this.selectedTeacher) {
        params.append('teacher_id', this.selectedTeacher);
      }
      if (this.selectedStatus) {
        params.append('status', this.selectedStatus);
      }

      // Create download link
      const link = document.createElement('a');
      link.href = `${this.$BACKEND_HOST}/api/downloadstatistics/payments?${params.toString()}`;
      link.download = 'fizetesi_statisztikak.xlsx';
      link.style.display = 'none';

      // Append to body and trigger download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
    },

  }
};
</script>

<style scoped>
.payment-stats-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.stats-header {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  color: white;
  text-align: center;
  box-shadow: var(--shadow-lg);
  margin-bottom: 2rem;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  margin-bottom: 0.5rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.stats-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.stats-header p {
  font-size: 0.95rem;
  opacity: 0.9;
  margin: 0;
}

.summary-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.summary-card {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
  padding: 1.75rem;
}

.summary-card.breakdown {
  background: var(--card-bg);
  color: var(--color-text);
}

.summary-card.breakdown .card-content h3 {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  color: var(--color-heading);
}

.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
}

.breakdown-col {
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid var(--color-border);
}

.breakdown-col h4 {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
}

.breakdown-col .stat-item {
  background: transparent;
  box-shadow: none;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--color-border);
}

.breakdown-col .stat-item:last-child {
  border-bottom: none;
}

.breakdown-col .stat-item .label {
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.breakdown-col .stat-item .value {
  color: var(--color-heading);
  font-size: 1.2rem;
}

.summary-card.overall {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  position: relative;
  overflow: hidden;
  padding: 2rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
}

.summary-card.overall::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, 
    rgba(255, 255, 255, 0.05) 0%, 
    transparent 50%, 
    rgba(255, 255, 255, 0.02) 100%);
  pointer-events: none;
  z-index: 0;
}

.summary-card.overall:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.summary-card.overall .card-content {
  position: relative;
  z-index: 1;
}

.summary-card.overall .card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.summary-card.overall .card-icon:hover {
  transform: scale(1.05) rotate(5deg);
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.summary-card.overall .card-content h3 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: -0.02em;
}

.summary-card.overall .stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
}

.summary-card.overall .stat-item {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.3s ease;
}

.summary-card.overall .stat-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.summary-card.overall .stat-item .label {
  font-size: 0.9rem;
  opacity: 0.9;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.summary-card.overall .stat-item .value {
  font-size: 1.4rem;
  font-weight: 800;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  letter-spacing: -0.01em;
}

.tab-search {
  padding: 1rem 2rem;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  gap: 1rem;
  align-items: center;
  background: var(--color-background-mute);
}

.tab-search .search-input-wrapper {
  position: relative;
  max-width: 400px;
  flex: 1;
}

.tab-search .search-input-wrapper svg {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
}

.filters-section {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
}

.search-group {
  margin-bottom: 1rem;
}

.search-input-wrapper {
  position: relative;
  max-width: 400px;
}

.search-input-wrapper svg {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
}

.modern-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
  font-size: 1rem;
}

.filter-options {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.date-filters {
  margin-top: 1rem;
  padding: 1rem;
  background-color: var(--color-background-mute);
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.date-filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.date-filter-group label {
  font-weight: 600;
  color: var(--color-heading);
  min-width: 120px;
}

.custom-date-inputs,
.month-year-selector {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date-input-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
}

.filter-select {
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
  min-width: 150px;
}

.stats-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  background: var(--color-background-mute);
  padding: 0.5rem;
  border-radius: 12px;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.tab-button:hover {
  background: var(--color-background);
  color: var(--color-text);
}

.tab-button.active {
  background: var(--primary-blue);
  color: white;
}

.tab-button.download-btn {
  background: var(--success);
  color: white;
  margin-left: auto;
}

.tab-button.download-btn:hover {
  background: var(--success-dark, #059669);
}

.stats-section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.section-header h2 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.children-count {
  color: var(--color-text-muted);
  font-weight: 500;
}

.stats-grid-large {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: 2rem;
}

.stat-card {
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid var(--color-border);
}

.stat-card h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.chart-placeholder {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.chart-bar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.bar-label {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  font-weight: 500;
}

.bar-container {
  position: relative;
  height: 24px;
  background: var(--color-border);
  border-radius: 12px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 12px;
  transition: width 0.3s ease;
}

.bar-value {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.status-item {
  text-align: center;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.status-item.completed {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.2);
}

.status-item.partial {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.2);
}

.status-item.pending {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
}

.status-label {
  display: block;
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
}

.status-count {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-heading);
}

/* Teacher Summary Styles */
.teacher-summary {
  margin-bottom: 2rem;
  padding: 0 2rem;
}

.teacher-summary .summary-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: var(--shadow-sm);
}

.teacher-summary .card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--primary-blue);
  border-radius: 8px;
  color: white;
}

.teacher-summary .card-content h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-top: 0;
}

.teacher-summary .stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.teacher-summary .stat-item {
  text-align: center;
}

.teacher-summary .stat-item .label {
  display: block;
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin-bottom: 0.25rem;
}

.teacher-summary .stat-item .value {
  display: block;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-heading);
}

.groups-grid, .teachers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  padding: 2rem;
}

.group-card, .teacher-card {
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
}

.group-card:hover, .teacher-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.group-header, .teacher-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.group-header h3, .teacher-header h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.group-count, .teacher-groups {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  font-weight: 500;
}

.group-stats, .teacher-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.group-stat, .teacher-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.group-stat .label, .teacher-stat .label {
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.group-stat .value, .teacher-stat .value {
  color: var(--color-heading);
  font-weight: 600;
}

.children-table {
  padding: 2rem;
}

.children-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.summary-block {
  background: var(--color-background-mute);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.25rem;
}

.summary-block h4 {
  margin: 0 0 1rem 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--primary-blue);
}

.summary-chips {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-chip {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 1rem;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--color-background);
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.chip-label {
  font-weight: 600;
  color: var(--color-heading);
}

.chip-paid {
  color: var(--color-success, #2e7d32);
  font-weight: 600;
}

.chip-billing {
  color: var(--primary-blue);
  font-weight: 600;
}

.no-summary {
  color: var(--color-text-muted);
  font-size: 0.9rem;
  padding: 0.5rem 0;
}

.col-billing {
  font-weight: 600;
}

.amount.billing {
  color: var(--primary-blue);
  font-weight: 600;
}

.table-header {
  display: grid;
  grid-template-columns: 50px 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-background-mute);
  border-radius: 8px;
  font-weight: 600;
  color: var(--color-heading);
  margin-bottom: 1rem;
}

.table-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.table-row {
  display: grid;
  grid-template-columns: 50px 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-background);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  align-items: center;
  transition: all 0.3s ease;
}

.table-row:hover {
  background: var(--color-background-mute);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.child-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.child-name {
  font-weight: 600;
  color: var(--color-heading);
}

.child-details {
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.group-badge {
  display: inline-block;
  background: var(--primary-blue);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.teacher-name {
  color: var(--color-text);
  font-size: 0.875rem;
}

.amount {
  font-weight: 600;
}

.amount.paid {
  color: var(--success);
}

.amount.due {
  color: var(--error);
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.completed {
  background: rgba(34, 197, 94, 0.1);
  color: var(--success);
}

.status-badge.partial {
  background: rgba(245, 158, 11, 0.1);
  color: var(--warning);
}

.status-badge.pending {
  background: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.btn-action {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-action:hover {
  background: var(--secondary-blue);
  transform: translateY(-1px);
}

/* Toast animations */
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .stats-grid-large {
    grid-template-columns: 1fr;
  }
  
  .groups-grid, .teachers-grid {
    grid-template-columns: 1fr;
  }
  
  .table-header, .table-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .table-header {
    display: none;
  }
  
  .table-row {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .summary-card.overall {
    padding: 1.5rem;
  }
  
  .summary-card.overall .stats-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .payment-stats-container {
    padding: 1rem;
  }
  
  .stats-header {
    padding: 1.5rem;
  }
  
  .stats-header h1 {
    font-size: 2rem;
  }
  
  .filter-options {
    flex-direction: column;
  }
  
  .stats-tabs {
    flex-wrap: wrap;
  }
  
  .tab-button {
    flex: 1;
    min-width: 120px;
  }
  
  .summary-card.overall {
    padding: 1.25rem;
    margin: 0 -0.5rem;
  }
  
  .summary-card.overall .card-icon {
    width: 56px;
    height: 56px;
    margin-bottom: 1rem;
  }
  
  .summary-card.overall .card-content h3 {
    font-size: 1.5rem;
  }
  
  .summary-card.overall .stat-item {
    padding: 1rem;
  }
  
  .summary-card.overall .stat-item .value {
   font-size: 1.2rem;
 }
}

/* Expense Statistics Styles */
.expense-stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stats-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.stats-card .card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 10px;
  color: white;
  flex-shrink: 0;
}

.stats-card .card-content h3 {
  color: var(--color-heading);
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.stats-card .stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-blue);
  margin: 0;
}

/* Chart Styles */
.chart-section {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
}

.chart-section h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
}

.category-chart, .monthly-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.chart-bar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.bar-label {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  font-weight: 500;
}

.bar-container {
  position: relative;
  height: 32px;
  background: var(--color-border);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 16px;
  transition: width 0.5s ease;
  position: relative;
}

.bar-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, rgba(255,255,255,0.2), transparent);
  border-radius: 16px;
}

.bar-value {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* Expenses table styles */
.expenses-table {
  padding: 2rem;
}

.expenses-table .table-header {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr 1.5fr;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-background-mute);
  border-radius: 8px;
  font-weight: 600;
  color: var(--color-heading);
  margin-bottom: 1rem;
}

.expenses-table .table-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.expenses-table .table-row {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr 1.5fr;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-background);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  align-items: center;
  transition: all 0.3s ease;
}

.expenses-table .table-row:hover {
  background: var(--color-background-mute);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.expenses-table .col-description {
  font-weight: 600;
  color: var(--color-heading);
}

.expenses-table .col-category .category-badge {
  background: var(--secondary-blue);
}

.expenses-table .col-amount .amount.expense {
  color: var(--error);
}

.expenses-table .col-date {
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.expenses-table .col-actions {
  display: flex;
  gap: 0.5rem;
}

.expenses-table .btn-edit {
  background: var(--warning);
  color: white;
}

.expenses-table .btn-edit:hover {
  background: var(--warning-dark, #d97706);
}

.expenses-table .btn-delete {
  background: var(--error);
  color: white;
}

.expenses-table .btn-delete:hover {
    background: var(--error-dark, #dc2626);
  }

.no-data {
  padding: 3rem 1rem;
  text-align: center;
  color: var(--color-text-muted);
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.no-data-content p {
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
}

.no-data-content small {
  opacity: 0.7;
}

/* Expand button styles */
.btn-expand {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.btn-expand:hover {
  background: var(--color-background-mute);
  color: var(--color-text);
}

.btn-expand.expanded svg {
  transform: rotate(90deg);
}

.col-expand {
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Payment details styles */
.payment-details-row {
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.payment-details-content {
  padding: 1.5rem 2rem;
  margin-left: 50px; /* Align with table content */
}

.payment-details-content h4 {
  color: var(--color-heading);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-top: 0;
}

.payments-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.payment-item {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.payment-info {
  flex: 1;
}

.payment-ticket {
  font-weight: 600;
  color: var(--color-heading);
  margin-bottom: 0.25rem;
}

.payment-date {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.payment-method {
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.payment-amounts {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;
}

.payment-paid {
  font-weight: 600;
  color: var(--success);
}

.payment-due {
  font-weight: 600;
  color: var(--error);
}

.payment-notes {
  color: var(--color-text-muted);
  font-style: italic;
  margin-top: 0.5rem;
}

.no-payments {
  text-align: center;
  color: var(--color-text-muted);
  font-style: italic;
  padding: 1rem;
}

/* Modal button styles */
.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: var(--secondary-blue);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: var(--color-border);
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--color-background);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  background: var(--color-background-mute);
}
/* Enhanced Download Button Styles */
.download-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.download-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  background: linear-gradient(135deg, var(--secondary-blue) 0%, var(--primary-blue) 100%);
}

.download-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.download-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg,
    rgba(255, 255, 255, 0.15) 0%,
    transparent 50%,
    rgba(255, 255, 255, 0.1) 100%);
  pointer-events: none;
  z-index: -1;
  opacity: 0.7;
}

.download-btn svg {
  transition: all 0.3s ease;
}

.download-btn:hover svg {
  transform: scale(1.1);
}

.download-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

/* Responsive adjustments for download button */
@media (max-width: 768px) {
  .download-btn {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
    gap: 0.5rem;
  }

  .download-btn svg {
    width: 18px;
    height: 18px;
  }
}
</style>
